/**
 * Curated skill + language catalogues for Add/Edit Position.
 * Source: recruiter-provided category groupings (Feb 2026).
 *
 * `TECH_SKILLS` and `LANGUAGES` are flat de-duplicated alphabetical lists used
 * by the searchable dropdown. `SKILL_CATEGORIES` keeps the original grouping
 * in case we want to expose category headers in the UI later.
 */

export const SKILL_CATEGORIES = {
  'Programming Languages': [
    'JavaScript', 'TypeScript', 'Python', 'Java', 'C', 'C++', 'C#', 'Go',
    'Rust', 'PHP', 'Ruby', 'Swift', 'Kotlin', 'Scala', 'R', 'MATLAB',
    'Dart', 'Perl', 'Objective-C', 'Visual Basic', 'VBA', 'COBOL',
    'Fortran', 'Groovy', 'Lua', 'Haskell', 'Elixir', 'Erlang', 'Julia',
    'Shell', 'PowerShell', 'SQL', 'PL/SQL', 'T-SQL', 'ABAP',
  ],
  Frontend: [
    'React.js', 'Next.js', 'Vue.js', 'Nuxt.js', 'Angular', 'Svelte',
    'SvelteKit', 'HTML', 'CSS', 'SCSS', 'Tailwind CSS', 'Bootstrap',
    'Material UI', 'Chakra UI', 'Redux', 'Zustand', 'Webpack', 'Vite',
    'jQuery', 'Three.js', 'D3.js',
  ],
  Backend: [
    'Node.js', 'Express.js', 'NestJS', 'Django', 'Flask', 'FastAPI',
    'Spring Boot', 'Spring Framework', '.NET', 'ASP.NET Core', 'Laravel',
    'Symfony', 'Ruby on Rails', 'Phoenix', 'Gin', 'Fiber', 'GraphQL',
    'REST API', 'gRPC', 'Microservices', 'Serverless',
  ],
  Databases: [
    'PostgreSQL', 'MySQL', 'MariaDB', 'SQL Server', 'Oracle Database',
    'MongoDB', 'Redis', 'Elasticsearch', 'OpenSearch', 'Cassandra',
    'DynamoDB', 'Firebase', 'Supabase', 'SQLite', 'Neo4j', 'Snowflake',
    'BigQuery', 'Redshift', 'ClickHouse', 'InfluxDB',
  ],
  Cloud: [
    'AWS', 'Microsoft Azure', 'Google Cloud Platform', 'IBM Cloud',
    'Oracle Cloud', 'Hetzner Cloud', 'DigitalOcean', 'Cloudflare',
    'AWS Lambda', 'Azure Functions', 'Google Cloud Functions',
    'EC2', 'S3', 'RDS', 'EKS', 'AKS', 'GKE',
  ],
  DevOps: [
    'Docker', 'Kubernetes', 'Terraform', 'Ansible', 'Helm', 'Jenkins',
    'GitHub Actions', 'GitLab CI/CD', 'Azure DevOps', 'CircleCI',
    'Argo CD', 'Prometheus', 'Grafana', 'Datadog', 'New Relic',
    'Linux', 'Nginx', 'Apache', 'CI/CD', 'Infrastructure as Code',
  ],
  SAP: [
    'SAP S/4HANA', 'SAP R/3', 'SAP FI/CO', 'SAP MM', 'SAP SD', 'SAP PP',
    'SAP PM', 'SAP EWM', 'SAP TM', 'SAP BW', 'SAP BW/4HANA',
    'SAP SuccessFactors', 'SAP HCM', 'SAP Basis', 'SAP ABAP',
    'SAP Fiori', 'SAP UI5', 'SAP BTP', 'SAP Ariba', 'SAP BRIM',
  ],
  Mainframe: [
    'z/OS', 'JCL', 'COBOL', 'REXX', 'ISPF', 'TSO', 'DB2 Mainframe',
    'CICS', 'IMS', 'BMC AMI DevX', 'Code Pipeline', 'Endevor',
    'Control-M', 'Natural', 'Adabas',
  ],
  'Testing & QA': [
    'Manual Testing', 'Test Automation', 'Selenium', 'Cypress',
    'Playwright', 'Jest', 'Mocha', 'JUnit', 'TestNG', 'Postman',
    'SoapUI', 'Jira Xray', 'Octane', 'ACCELQ', 'UAT', 'Regression Testing',
    'Integration Testing', 'Performance Testing', 'Load Testing',
    'Test Data Management', 'Test Environment Management',
  ],
  'Data & AI': [
    'Machine Learning', 'Deep Learning', 'Generative AI', 'LLMs',
    'OpenAI API', 'TensorFlow', 'PyTorch', 'Scikit-learn', 'Pandas',
    'NumPy', 'Apache Spark', 'Databricks', 'Airflow', 'dbt',
    'Power BI', 'Tableau', 'Looker', 'Data Engineering',
    'Data Science', 'MLOps', 'NLP', 'Computer Vision',
  ],
  Cybersecurity: [
    'IAM', 'SIEM', 'SOC', 'Penetration Testing', 'Vulnerability Management',
    'ISO 27001', 'NIST', 'GDPR', 'Zero Trust', 'Firewall Management',
    'Endpoint Security', 'Cloud Security', 'Application Security',
    'Security Architecture', 'Risk Management',
  ],
  'Business Domains': [
    'Banking', 'Insurance', 'Automotive', 'Aviation', 'Railway',
    'Healthcare', 'Pharmaceuticals', 'Logistics', 'Manufacturing',
    'Energy', 'Telecommunications', 'Retail', 'E-Commerce',
    'Public Sector', 'Defense', 'FinTech', 'MedTech', 'Construction',
    'Real Estate', 'Education', 'Consulting',
  ],
  'Non-IT Skills': [
    'Recruitment', 'Active Sourcing', 'Executive Search', 'Sales',
    'Business Development', 'Account Management', 'Project Management',
    'Stakeholder Management', 'Process Management', 'Change Management',
    'People Management', 'Leadership', 'Negotiation', 'Customer Service',
    'Finance', 'Controlling', 'Accounting', 'Procurement', 'Supply Chain',
    'Marketing', 'Employer Branding', 'HR Management', 'Payroll',
    'Legal', 'Compliance', 'Operations', 'Strategy', 'Administration',
  ],
  'Soft Skills': [
    'Communication', 'Problem Solving', 'Analytical Thinking',
    'Teamwork', 'Ownership', 'Creativity', 'Adaptability',
    'Time Management', 'Presentation Skills', 'Decision Making',
    'Conflict Resolution', 'Emotional Intelligence', 'Resilience',
    'Intercultural Communication',
  ],
};

const _LANGUAGES = [
  'German', 'English', 'French', 'Spanish', 'Italian', 'Portuguese',
  'Dutch', 'Polish', 'Turkish', 'Arabic', 'Chinese', 'Japanese',
  'Korean', 'Hindi', 'Russian', 'Ukrainian', 'Swahili',
];

const _uniqSort = (arr) =>
  Array.from(new Set(arr.map((s) => s.trim()))).sort((a, b) =>
    a.localeCompare(b)
  );

export const TECH_SKILLS = _uniqSort(Object.values(SKILL_CATEGORIES).flat());
export const LANGUAGES = _uniqSort(_LANGUAGES);
